import Model from "../../models/model";
import store from "../store";
import RepoFactory from "./repoFactory";
import IRepoStore from "../../interfaces/repoStore";
import IRepo from "../../interfaces/repo";
import {ObjectLoadStrategy} from "./objectLoadStrategy";
import IProvider from "../../interfaces/provider";
import {Constructor} from "../../../global/constructor";
import {IServerData} from "~/interfaces/serverInterfaces";
import {TToBoolean} from "~/interfaces/generalInterfaces";
import {InstanceHelper} from '~/helpers/instanceHelper'

interface RepoOptions {
  onFirstAdd?: (item) => any;
  onAdd?: (item) => any;
}

export default class Repo<T extends Model, TProvider extends IProvider<T> = IProvider<T>> implements IRepo<T, TProvider> {
  protected _store: IRepoStore<T>;

  constructor(protected repoName: string, protected modelType: Constructor<T>, protected options: RepoOptions = {}, public provider?: TProvider) {
  }

  get store(): IRepoStore<T> {
    if (!this._store) {
      if (this.repoName) {
        this._store = <IRepoStore<T>>store.state[this.repoName];
        if (!this._store) {
          store.registerModule(this.repoName, ((new RepoFactory<T>(this)).module()));
          this._store = <IRepoStore<T>>store.state[this.repoName];
        }
      }
    }
    return this._store;
  }

  find(id: string): T {
    return this.getterMethod('find')(id);
  }

  findBy(callback: TToBoolean<T>): T {
    return this.getterMethod('findBy')(callback);
  }

  findById(...ids: string[]): T {
    return this.getterMethod('findById')(...ids);
  }

  findWhere(callback: TToBoolean<T>): T[] {
    return this.getterMethod('findWhere')(callback);
  }

  findOrCreate(...idsToCheck: string[]): T {
    let found
    for (const id of idsToCheck) {
      found = this.find(id)
      if (found) break
    }
    return found ? found : new this.modelType();
  }

  findOrCreateWith(ids: string | string[], ...newArgs: any[]): T {
    ids = InstanceHelper.toArray(ids)
    let found = this.findById(...ids)
    return found ?? new this.modelType(...newArgs);
  }

  all(loadStrategies: ObjectLoadStrategy[] = [ObjectLoadStrategy.FromMemory], opts: object = {}): T[] {
    let results: T[];

    if (loadStrategies.includes(ObjectLoadStrategy.FromMemory)) {
      results = this.getterMethod('all')();
    }

    return results;
  }

  async allAsync(loadStrategies: ObjectLoadStrategy[] = [ObjectLoadStrategy.FromMemory], opts: object = {}): Promise<T[]> {
    let results: T[] = this.all(loadStrategies, opts);

    if (loadStrategies.includes(ObjectLoadStrategy.FromServer)) {
      if (this.provider) {
        const [response] = await this.provider.fetchAll(opts)
        const objs = this.provider.createOrUpdateManyFrom(response[this.provider.collectionRoot]);
        results.length = 0;
        results.push(...objs);
        return results;
      }
    }
    return results;
  }

  async save(obj: T, objJson?: object): Promise<IServerData> {
    return this.dispatch('save', {obj, objJson});
  }

  async destroy(obj: T): Promise<IServerData> {
    return this.dispatch('destroy', {obj});
  }

  any(callback?: TToBoolean<T>): boolean {
    return this.whereOrAll(callback).length > 0
  }

  empty(callback?: TToBoolean<T>): boolean {
    return this.whereOrAll(callback).length === 0
  }

  commit(mutation: string, payload?: any) {
    if (!this.store) return null;
    if (payload) {
      store.commit(`${this.repoName}/${mutation}`, payload);
    } else {
      store.commit(`${this.repoName}/${mutation}`);
    }
  }

  async dispatch(action: string, payload?: any) {
    if (!this.store) return null;
    return store.dispatch(`${this.repoName}/${action}`, payload);
  }

  getterMethod(getter: string): Function {
    if (!this.store) return null;
    return store.getters[`${this.repoName}/${getter}`];
  }

  addForKey(key: string, item: T): void {
    this.commit('addForKey', { key: key, item: item});
  }

  add(item: T): T {
    this.commit('add', item);
    if (this.options && this.options.onFirstAdd) {
      this.options.onFirstAdd(item);
      delete this.options.onFirstAdd;
    }
    return item;
  }

  addMany(items: T[]): T[] {
    for (const item of items) {
      this.add(item);
    }
    return items;
  }

  addFromJson(itemJson: any): T {
    let item = this.findOrCreate(itemJson.client_id, itemJson.id);
    item.loadFromJson(itemJson);
    this.add(item);
    return item;
  }

  addManyFromJson(itemsJson: any[]): T[] {
    const items = [];
    for (const itemJson of itemsJson) {
      items.push(this.addFromJson(itemJson));
    }
    return items;
  }

  remove(item: T): void {
    this.commit('remove', item);
  }

  removeAll(): void {
    this.all().forEach(item => this.remove(item))
  }

  removeWhereKey(key: string): void {
    this.commit('removeWhereKey', key);
  }

  private whereOrAll(callback?: TToBoolean<T>): T[] {
    if (typeof callback === 'function') {
      return this.findWhere(callback)
    } else {
      return this.all()
    }
  }
}